import React, { useEffect, useMemo, useState } from 'react';
import { Table, message, Typography, Tag, Space, Button } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { Task } from '../services/tasks';
import { customerAuthService } from '../services/customerAuth';
import customerApi from '../services/customerApi';
import { useTableColumnSearch } from '../hooks/useTableColumnSearch';
import { useResizableColumns } from '../hooks/useResizableColumns';
import { useColumnVisibility } from '../hooks/useColumnVisibility';
import { ResizableTableHeader } from '../components/ResizableTableHeader';
import { ColumnVisibilityToggle } from '../components/ColumnVisibilityToggle';
import '../styles/resizable-table.css';

const { Title } = Typography;

type FilterType = 'all' | 'ongoing' | 'complete';

export const CustomerTasks: React.FC = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<FilterType>('all');
  const customer = customerAuthService.getCurrentCustomer();
  const customerId = customer?.id;
  const navigate = useNavigate();

  useEffect(() => {
    if (customerId) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const fetchData = async () => {
    if (!customer?.id) return;
    
    setLoading(true);
    try {
      const response = await customerApi.get('/customer/tasks');
      setTasks(response.data.data || []);
    } catch (error: any) {
      console.error('Failed to load tasks:', error);
      let errorMessage = 'Failed to load tasks';
      
      if (error.response) {
        // Server responded with error
        errorMessage = error.response.data?.message || error.response.data?.error || `Server error: ${error.response.status}`;
        if (error.response.status === 401) {
          errorMessage = 'Unauthorized. Please login again.';
        } else if (error.response.status === 403) {
          errorMessage = 'Access denied.';
        } else if (error.response.status === 404) {
          errorMessage = 'Tasks endpoint not found.';
        } else if (error.response.status >= 500) {
          errorMessage = 'Server error. Please try again later.';
        }
      } else if (error.request) {
        // Request was made but no response received
        errorMessage = 'Network error: Unable to connect to server';
      } else {
        // Something else happened
        errorMessage = error.message || 'Unknown error occurred';
      }
      
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatTaskNumber = (id: number) => {
    return `#${id.toString().padStart(7, '0')}`;
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'Plan':
        return 'blue';
      case 'Progress':
        return 'orange';
      case 'Review':
        return 'purple';
      case 'Approve':
        return 'cyan';
      case 'Completed':
        return 'green';
      default:
        return 'default';
    }
  };

  const handleApprove = async (taskId: number, e?: React.MouseEvent) => {
    e?.stopPropagation(); // Prevent row click
    try {
      // Create a review to indicate customer approval
      // This will automatically trigger the status change to "Approve" in the backend
      await customerApi.post('/customer/task-reviews', {
        task_id: taskId,
        customer_id: customer?.id,
        review: 'Approved by customer',
      });
      
      message.success('Task approved successfully');
      fetchData();
    } catch (error: any) {
      console.error('Failed to approve task:', error);
      let errorMessage = 'Failed to approve task';
      
      if (error.response) {
        errorMessage = error.response.data?.message || error.response.data?.error || `Server error: ${error.response.status}`;
        if (error.response.status === 401) {
          errorMessage = 'Unauthorized. Please login again.';
        } else if (error.response.status === 403) {
          errorMessage = 'Access denied.';
        }
      } else if (error.request) {
        errorMessage = 'Network error: Unable to connect to server';
      } else {
        errorMessage = error.message || 'Unknown error occurred';
      }
      
      message.error(errorMessage);
    }
  };

  const filteredTasks = useMemo(() => {
    if (filter === 'all') {
      return tasks;
    } else if (filter === 'ongoing') {
      return tasks.filter(task => task.status && ['Plan', 'Progress', 'Review', 'Approve'].includes(task.status));
    } else if (filter === 'complete') {
      return tasks.filter(task => task.status === 'Completed');
    }
    return tasks;
  }, [tasks, filter]);

  const baseColumns = [
    {
      title: 'Task #',
      key: 'task_number',
      width: 120,
      sorter: (a: Task, b: Task) => a.id - b.id,
      render: (_: unknown, record: Task) => formatTaskNumber(record.id),
    },
    {
      title: 'Type',
      dataIndex: 'taskType',
      key: 'taskType',
      width: 150,
      sorter: (a: Task, b: Task) => (a.taskType?.name || '').localeCompare(b.taskType?.name || ''),
      render: (taskType: any) => taskType?.name || '-',
    },
    {
      title: 'Template',
      dataIndex: 'taskTemplate',
      key: 'taskTemplate',
      width: 180,
      sorter: (a: Task, b: Task) => (a.taskTemplate?.name || '').localeCompare(b.taskTemplate?.name || ''),
      render: (taskTemplate: any) => taskTemplate?.name || '-',
    },
    {
      title: 'Year',
      dataIndex: 'year',
      key: 'year',
      width: 100,
      sorter: (a: Task, b: Task) => (a.year || 0) - (b.year || 0),
      render: (year: number) => year || '-',
    },
    {
      title: 'Create Date',
      dataIndex: 'created_date',
      key: 'created_date',
      width: 120,
      sorter: (a: Task, b: Task) => {
        const dateA = a.created_date ? new Date(a.created_date).getTime() : 0;
        const dateB = b.created_date ? new Date(b.created_date).getTime() : 0;
        return dateA - dateB;
      },
      render: (date: string) => date ? new Date(date).toLocaleDateString() : '-',
    },
    {
      title: 'Due Date',
      dataIndex: 'due_date',
      key: 'due_date',
      width: 120,
      sorter: (a: Task, b: Task) => {
        const dateA = a.due_date ? new Date(a.due_date).getTime() : 0;
        const dateB = b.due_date ? new Date(b.due_date).getTime() : 0;
        return dateA - dateB;
      },
      render: (date: string) => date ? new Date(date).toLocaleDateString() : '-',
    },
    {
      title: 'Status',
      key: 'status',
      width: 120,
      sorter: (a: Task, b: Task) => (a.status || '').localeCompare(b.status || ''),
      render: (_: unknown, record: Task) => (
        <Tag color={getStatusColor(record.status)}>
          {record.status || 'Plan'}
        </Tag>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: 120,
      render: (_: unknown, record: Task) => {
        // Show approve button only when status is "Review"
        if (record.status === 'Review') {
          return (
            <Button
              type="primary"
              icon={<CheckOutlined />}
              onClick={(e) => handleApprove(record.id, e)}
              size="small"
            >
              Approve
            </Button>
          );
        }
        return null;
      },
    },
  ];

  const {
    columnConfig,
    visibleColumns,
    visibleKeys,
    handleColumnVisibilityChange,
  } = useColumnVisibility(baseColumns);

  const { columns: resizableColumns } = useResizableColumns(visibleColumns);

  const memoizedColumns = useMemo(
    () =>
      resizableColumns.map((col) => ({
        ...col,
        width: col.width, // Preserve width
        onHeaderCell: col.onHeaderCell, // Preserve onHeaderCell for resizing
        getSearchValue:
          col.key === 'task_number'
            ? (record: Task) => formatTaskNumber(record.id)
            : 'dataIndex' in col && col.dataIndex === 'taskType'
            ? (record: Task) => record.taskType?.name
            : 'dataIndex' in col && col.dataIndex === 'taskTemplate'
            ? (record: Task) => record.taskTemplate?.name
            : 'dataIndex' in col && col.dataIndex === 'year'
            ? (record: Task) => record.year
            : 'dataIndex' in col && col.dataIndex === 'created_date'
            ? (record: Task) => record.created_date
            : 'dataIndex' in col && col.dataIndex === 'due_date'
            ? (record: Task) => record.due_date
            : col.key === 'status'
            ? (record: Task) => record.status || 'Plan'
            : (undefined as unknown as (record: Task) => string | number | null | undefined),
      })),
    [resizableColumns]
  );

  const { columnsWithSearch, filteredData } = useTableColumnSearch<Task>(filteredTasks, memoizedColumns as any);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0 }}>My Tasks</Title>
        <ColumnVisibilityToggle
          columns={baseColumns}
          visibleKeys={visibleKeys}
          onChange={handleColumnVisibilityChange}
        />
      </div>

      {/* Filter Buttons */}
      <Space style={{ marginBottom: 16 }}>
        <Button
          type={filter === 'all' ? 'primary' : 'default'}
          onClick={() => setFilter('all')}
        >
          All
        </Button>
        <Button
          type={filter === 'ongoing' ? 'primary' : 'default'}
          onClick={() => setFilter('ongoing')}
        >
          Ongoing
        </Button>
        <Button
          type={filter === 'complete' ? 'primary' : 'default'}
          onClick={() => setFilter('complete')}
        >
          Complete
        </Button>
      </Space>

      <Table
        columns={columnsWithSearch}
        dataSource={filteredData}
        rowKey="id"
        loading={loading}
        pagination={false}
        onRow={(record) => ({
          onClick: (e) => {
            // Don't navigate if clicking on a button or action element
            const target = e.target as HTMLElement;
            if (target.closest('button') || target.closest('.ant-btn')) {
              return;
            }
            navigate(`/customer/tasks/${record.id}`);
          },
          style: { cursor: 'pointer' },
        })}
        components={{
          header: {
            cell: ResizableTableHeader,
          },
        }}
        footer={() => (
          <div style={{ textAlign: 'right', padding: '8px 0' }}>
            <Typography.Text strong>
              Total: {filteredTasks.length} task{filteredTasks.length !== 1 ? 's' : ''}
            </Typography.Text>
          </div>
        )}
      />
    </div>
  );
};

